import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "../../lib/api";
import { formatDate } from "../../lib/dateFormat";
import { Button } from "../ui/button";

function StatusBadge({ status }) {
  const palette = {
    Closed: { bg: "rgba(143,143,154,0.18)", color: "#52525B" },
    "In Progress": { bg: "rgba(20,184,184,0.18)", color: "#0E7B7B" },
    "Under Investigation": { bg: "rgba(139,92,246,0.18)", color: "#6D28D9" },
    "Pending Follow-up": { bg: "rgba(225,42,251,0.16)", color: "#A21CAF" },
    Escalated: { bg: "rgba(225,72,77,0.14)", color: "#B91C1C" },
    New: { bg: "rgba(59,130,246,0.18)", color: "#1D4ED8" },
  };
  const s = palette[status] || palette.New;
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      {status}
    </span>
  );
}

export default function SimilarCasesPanel({ caseId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get(`/cases/${caseId}/similar?limit=5`)
      .then(({ data }) => {
        if (!cancelled) setItems(data.items || []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId]);

  return (
    <div className="brand-card" data-testid="similar-cases-panel">
      <div className="flex items-center justify-between border-b border-[color:var(--border-subtle)] px-5 py-4">
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--page-fg)]">
            Similar past cases
          </h3>
          <p className="text-[11px] text-[color:var(--page-fg-muted)]">
            Top 5 closed cases by similarity (deterministic — no LLM)
          </p>
        </div>
      </div>
      <div className="px-5 py-3">
        {loading && (
          <p className="py-4 text-center text-sm text-[color:var(--page-fg-muted)]">Searching…</p>
        )}
        {!loading && items.length === 0 && (
          <p
            className="py-4 text-center text-sm text-[color:var(--page-fg-muted)]"
            data-testid="similar-cases-empty"
          >
            No closed cases match this profile yet.
          </p>
        )}
        {items.map((c) => (
          <div
            key={c.id}
            className="border-b border-[color:var(--border-subtle)] py-3 last:border-b-0"
            data-testid={`similar-case-row-${c.id}`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="flex flex-1 items-center gap-3">
                <Link
                  to={`/cases/${c.id}`}
                  className="font-mono text-sm font-semibold text-brand-teal hover:underline"
                >
                  {c.case_id}
                </Link>
                <StatusBadge status={c.status} />
                <span className="text-xs text-[color:var(--page-fg)]">
                  {c.issue_type} · {c.specific_issue}
                </span>
              </div>
              <div className="flex items-center gap-3 text-[11px] text-[color:var(--page-fg-muted)]">
                {typeof c.days_to_close === "number" && (
                  <span data-testid={`similar-days-${c.id}`}>
                    {c.days_to_close} days to close
                  </span>
                )}
                <span
                  className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] px-2 py-0.5 font-semibold text-brand-teal"
                  data-testid={`similar-score-${c.id}`}
                >
                  {c.similarity_score}/100
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setExpanded(expanded === c.id ? null : c.id)}
                  className="h-6 px-2"
                  data-testid={`similar-toggle-${c.id}`}
                >
                  <ChevronRight
                    className={`h-3.5 w-3.5 transition-transform ${
                      expanded === c.id ? "rotate-90" : ""
                    }`}
                  />
                </Button>
              </div>
            </div>
            {expanded === c.id && (
              <div
                className="mt-3 grid grid-cols-1 gap-3 rounded-lg bg-[color:var(--surface-2)] p-3 text-xs sm:grid-cols-2"
                data-testid={`similar-detail-${c.id}`}
              >
                <div>
                  <p className="text-[color:var(--page-fg-muted)]">Outcome</p>
                  <p className="font-medium text-[color:var(--page-fg)]">
                    {c.outcome || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[color:var(--page-fg-muted)]">Service line</p>
                  <p className="font-medium text-[color:var(--page-fg)]">
                    {c.service_line || "—"}
                  </p>
                </div>
                <div>
                  <p className="text-[color:var(--page-fg-muted)]">Reported</p>
                  <p className="font-medium text-[color:var(--page-fg)]">
                    {formatDate(c.date_reported)}
                  </p>
                </div>
                <div>
                  <p className="text-[color:var(--page-fg-muted)]">Closed</p>
                  <p className="font-medium text-[color:var(--page-fg)]">
                    {formatDate(c.investigation_end_date)}
                  </p>
                </div>
                {c.closure_summary_excerpt && (
                  <div className="sm:col-span-2">
                    <p className="text-[color:var(--page-fg-muted)]">Closure summary excerpt</p>
                    <p className="mt-0.5 italic text-[color:var(--page-fg)]">
                      "{c.closure_summary_excerpt}"
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
